import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { AirQualityService } from "../../services/airQualityService";
import { getLocationsApi, getLocationDetailApi } from "../../lib/api/location";
import { getDeviceListApi } from "../../lib/api/device";
import { regions } from "../../constants/regions";
import "./Stat.css";

const StatRegionView = () => {
  // 지역 선택 상태
  const [selectedRegion, setSelectedRegion] = useState("남평읍");

  // 차트 데이터
  const [chartData, setChartData] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState("복합악취");
  // 기본 날짜를 어제~오늘로 설정
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };
  
  const getToday = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getYesterday());
  const [endDate, setEndDate] = useState(getToday());
  const [loading, setLoading] = useState(false);
  const [regionData, setRegionData] = useState([]);
  const [locationStats, setLocationStats] = useState({ locationCount: 0, deviceCount: 0 });

  const airQualityService = AirQualityService.getInstance();

  // 나주시 지역 목록 (regions.ts에서 가져오기)
  const najuRegions = regions
    .filter(region => 
      region.code.startsWith("4617") && 
      region.code.endsWith("000") && 
      region.code !== "4617000000"
    )
    .sort((a, b) => {
      const order = ["면", "읍", "동"];
      const aType = order.findIndex(type => a.name.endsWith(type));
      const bType = order.findIndex(type => b.name.endsWith(type));
      if (aType !== bType) return aType - bType;
      return a.name.localeCompare(b.name);
    })
    .map(region => region.name);

  // 지역별 데이터 로드
  const loadRegionData = async () => {
    try {
      setLoading(true);
      
      // 해당 지역의 사업장 목록 조회
      const locationResponse = await getLocationsApi({
        size: 100,
        page: 0
      });
      
      console.log(`Loading data for region: ${selectedRegion}`);
      
      // 선택된 지역에 속한 사업장 필터링 (더 넓은 범위로 검색)
      const regionLocations = locationResponse.content.filter(location => {
        const matchesByFullAddress = location.fullAddress?.includes(selectedRegion);
        const matchesByDistrict = location.district === selectedRegion;
        const matchesByCity = location.city?.includes(selectedRegion);
        const matchesByProvince = location.province?.includes(selectedRegion);
        
        const matches = matchesByFullAddress || matchesByDistrict || matchesByCity || matchesByProvince;
        
        if (matches) {
          console.log(`Found matching location for ${selectedRegion}:`, {
            locationName: location.locationName,
            fullAddress: location.fullAddress,
            district: location.district,
            city: location.city,
            province: location.province
          });
        }
        
        return matches;
      });
      
      console.log(`Found ${regionLocations.length} locations in ${selectedRegion}:`, regionLocations);
      
      const processedData = [];
      let totalDevices = 0;
      
      // 각 사업장의 상세 정보와 센서 데이터 수집
      for (const location of regionLocations) {
        try {
          const locationDetail = await getLocationDetailApi(location.id);
          console.log(`Processing location: ${locationDetail.locationName}`, locationDetail);
          
          if (locationDetail.devices && locationDetail.devices.length > 0) {
            const activeDevices = locationDetail.devices.filter(device => device.status === 'ACTIVE');
            totalDevices += activeDevices.length;
            
            // 각 기기의 센서 데이터 수집
            for (const device of activeDevices) {
              try {
                const deviceTableId = device.device?.id;
                console.log(`Trying to load data for device: ${device.deviceCode} (table ID: ${deviceTableId})`);
                
                let sensorData = [];
                
                // 1. 먼저 device.device.id (실제 device table ID)로 시도
                if (deviceTableId) {
                  try {
                    sensorData = await airQualityService.getDeviceSensorData(
                      deviceTableId,
                      startDate,
                      endDate
                    );
                    console.log(`Data found for device table ID ${deviceTableId}:`, sensorData.length);
                  } catch (error) {
                    console.log(`No data for device table ID ${deviceTableId}`);
                  }
                }
                
                if (sensorData && sensorData.length > 0) {
                  processedData.push(...sensorData.map(data => ({
                    ...data,
                    locationName: locationDetail.locationName,
                    locationId: locationDetail.id
                  })));
                }
              } catch (error) {
                console.error(`Failed to load data for device ${device.deviceCode}:`, error);
              }
            }
          }
        } catch (error) {
          console.error(`Failed to load detail for location ${location.id}:`, error);
        }
      }
      
      console.log(`Processed ${processedData.length} sensor readings from ${totalDevices} devices in ${regionLocations.length} locations`);
      
      setRegionData(processedData);
      generateChartDataFromReal(processedData);
      
      setLocationStats({
        locationCount: regionLocations.length,
        deviceCount: totalDevices
      });
      
    } catch (error) {
      console.error('Failed to load region data:', error);
      setChartData([]); // 에러 시에도 빈 차트 표시
      setRegionData([]);
      setLocationStats({ locationCount: 0, deviceCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  // 실제 데이터로부터 차트 데이터 생성
  const generateChartDataFromReal = (data: any[]) => {
    if (!data || data.length === 0) {
      // 데이터가 없으면 빈 차트 표시
      setChartData([]);
      return;
    }

    // 시간대별 평균 계산
    const hourlyData = {};
    
    data.forEach(item => {
      const time = new Date(item.measuredAt);
      const hour = time.getHours();
      const hourKey = hour.toString().padStart(2, "0") + "시";
      
      if (!hourlyData[hourKey]) {
        hourlyData[hourKey] = {
          time: hourKey,
          values: [],
          h2s: [],
          nh3: [],
          ch4: [],
          temperature: [],
          humidity: []
        };
      }
      
      // 메트릭별 값 계산
      let value;
      switch (selectedMetric) {
        case "복합악취":
          // 복합악취는 외부 API에서 추후 제공 예정으로 현재는 0으로 표기
          value = 0;
          break;
        case "황화수소":
          value = item.h2s || 0;
          hourlyData[hourKey].h2s.push(value);
          break;
        case "암모니아":
          value = item.nh3 || 0;
          hourlyData[hourKey].nh3.push(value);
          break;
        case "메탄가스":
          value = item.ch4 || 0;
          hourlyData[hourKey].ch4.push(value);
          break;
        case "온도":
          value = item.temperature || 0;
          hourlyData[hourKey].temperature.push(value);
          break;
        case "습도":
          value = item.humidity || 0;
          hourlyData[hourKey].humidity.push(value);
          break;
        case "풍향":
          value = item.windDirection || 0;
          hourlyData[hourKey].windDirection = hourlyData[hourKey].windDirection || [];
          hourlyData[hourKey].windDirection.push(value);
          break;
        case "풍속":
          value = item.windSpeed || 0;
          hourlyData[hourKey].windSpeed = hourlyData[hourKey].windSpeed || [];
          hourlyData[hourKey].windSpeed.push(value);
          break;
        default:
          value = 0;
      }
      
      hourlyData[hourKey].values.push(value);
    });
    
    // 평균 계산하여 차트 데이터 생성
    const chartData = Object.values(hourlyData).map((hourData: any) => {
      const avgValue = hourData.values.length > 0 
        ? hourData.values.reduce((sum, val) => sum + val, 0) / hourData.values.length
        : 0;
        
      return {
        time: hourData.time,
        value: Number(avgValue.toFixed(3))
      };
    }).sort((a, b) => parseInt(a.time) - parseInt(b.time));
    
    setChartData(chartData);
  };

  // 데이터 로드 (selectedMetric 제거하여 탭 변경시 API 호출 방지)
  useEffect(() => {
    loadRegionData();
  }, [selectedRegion, startDate, endDate]);

  // 메트릭 변경시 기존 데이터로 차트 재생성 (API 호출 없음)
  useEffect(() => {
    if (regionData.length > 0) {
      generateChartDataFromReal(regionData);
    }
  }, [selectedMetric]);

  const generateChartData = () => {
    const data = [];
    for (let i = 0; i < 24; i++) {
      const hour = i.toString().padStart(2, "0");
      let value;

      switch (selectedMetric) {
        case "복합악취":
          value = 0.2 + Math.random() * 1.3;
          break;
        case "황화수소":
          value = 7.5 + Math.random() * 2;
          break;
        case "암모니아":
          value = 0.5 + Math.random() * 1.5;
          break;
        case "메탄가스":
          value = 100 + Math.random() * 60;
          break;
        case "온도":
          value = 25 + Math.random() * 5;
          break;
        case "습도":
          value = 0;
          break;
        case "풍향":
          value = Math.random() * 360;
          break;
        case "풍속":
          value = 0.5 + Math.random() * 0.5;
          break;
      }

      data.push({
        time: `${hour}시`,
        value: value,
      });
    }
    setChartData(data);
  };

  const handleExcelDownload = () => {
    console.log("엑셀 다운로드");
  };

  // CustomTooltip 컴포넌트에 타입 추가
  interface CustomTooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string;
  }

// 차트 커스텀 툴팁
  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
          <div className="chart-tooltip">
            <p style={{ margin: 0, whiteSpace: 'pre-line', color: 'white' }}>
              {`${label}\n값: ${payload[0].value.toFixed(2)}`}
            </p>
          </div>
      );
    }
    return null;
  };

  return (
    <div className="device-list-container">
      {/* 탭 버튼 */}
      <div className="tab-buttons">
        <div className="" style={{ float: "left" }}>
          <button
            className="tab-button"
            onClick={() => {
              window.location.href = "/stat";
            }}
          >
            나주시 시간대별 종합수치
          </button>
          <button
            className="tab-button active"
            onClick={() => {
              window.location.href = "/stat/region";
            }}
          >
            지역 시간대별 종합수치
          </button>
          <button
            className="tab-button"
            onClick={() => {
              window.location.href = "/stat/location";
            }}
          >
            사업장 시간대별 종합수치
          </button>
        </div>
        <a
          href={"javascript:;"}
          className="btn-excel-download"
          onClick={handleExcelDownload}
          style={{ float: "right" }}
        >
          <img src={"/img/btn_excel01.png"} />
        </a>
      </div>

      <div className="dlv_dv01">
        <div className="stat-region-content">
          {/* 왼쪽 지역 메뉴 */}
          <div className="region-sidebar-inner">
            <div className="region-header">
              <h3>조회대상</h3>
            </div>
            <div className="region-list">
              <ul className="region-items">
                {najuRegions.map((region) => (
                  <li
                    key={region}
                    className={selectedRegion === region ? "active" : ""}
                    onClick={() => setSelectedRegion(region)}
                  >
                    {region}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* 오른쪽 콘텐츠 영역 */}
          <div className="stat-content-right">
            <div className="env-content">
              {/* 지역명 표시 */}
              <div className="region-title">
                <span className="region-dot">●</span> {selectedRegion}
                <span className="region-subtitle"> 사업장 {locationStats.locationCount}곳 기기 {locationStats.deviceCount}개 종합수치</span>
              </div>

              <div className="date-selector">
                <span>조회일</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="date-input"
                />
                <span className="date-separator">~</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-input"
                />
              </div>

              <div className="table-section">
                <table className="device-table">
                  <thead>
                    <tr>
                      <th>측정일자</th>
                      <th>측정시간</th>
                      <th>복합악취(배수)</th>
                      <th>황화수소(ppm)</th>
                      <th>암모니아(ppm)</th>
                      <th>메탄가스(ppm)</th>
                      <th>온도</th>
                      <th>습도</th>
                      <th>풍향</th>
                      <th>풍속</th>
                      <th>등급</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={11}>데이터 로딩 중...</td>
                      </tr>
                    ) : regionData.length > 0 ? (
                      regionData.map((data, i) => {
                        const date = new Date(data.measuredAt);
                        return (
                          <tr key={i}>
                            <td>{date.toLocaleDateString('ko-KR')}</td>
                            <td>{date.getHours().toString().padStart(2, '0')}시</td>
                            <td>0</td>
                            <td>{(data.h2s || 0).toFixed(2)}</td>
                            <td>{(data.nh3 || 0).toFixed(2)}</td>
                            <td>{(data.ch4 || 0).toFixed(2)}</td>
                            <td>{(data.temperature || 0).toFixed(1)}°C</td>
                            <td>{(data.humidity || 0).toFixed(1)}%</td>
                            <td>-</td>
                            <td>-</td>
                            <td>{data.compositeGrade || "좋음"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={12}>해당 지역에 데이터가 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="metric-buttons">
                {["복합악취", "황화수소", "암모니아", "메탄가스", "온도", "습도", "풍향", "풍속"].map(
                  (metric) => (
                    <button
                      key={metric}
                      className={`metric-btn ${selectedMetric === metric ? "active" : ""}`}
                      onClick={() => setSelectedMetric(metric)}
                    >
                      {metric}
                    </button>
                  )
                )}
              </div>

              <div className="chart-container">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="time" stroke="#666" tick={{ fill: "#999", fontSize: 12 }} />
                      <YAxis
                        stroke="#666"
                        tick={{ fill: "#999", fontSize: 12 }}
                        domain={[0, "dataMax + 1"]}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#1dd3d6"
                        strokeWidth={2}
                        dot={{ fill: "#1dd3d6", r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ 
                    height: 300, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: '#666',
                    fontSize: '16px'
                  }}>
                    표시할 데이터가 없습니다.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatRegionView;
